// Piezas compartidas de las apps de gobierno (Salud · DIF y Tablero del Presidente).
// Solo tokens semánticos; los colores de estado van en chips con contraste AA.

import type { ReactNode } from 'react';
import { FlaskConical, ShieldCheck } from 'lucide-react';

import type { Origen } from '../types';

/** «Demostración» / «Datos reales» / etc. — SIEMPRE visible junto a la cifra. */
export function OrigenBadge({ origen, className = '' }: { origen: Origen | string; className?: string }) {
  const meta: Record<string, { label: string; cls: string; title: string }> = {
    demostracion: { label: 'Demostración', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
      title: 'Datos sintéticos de demostración (is_demo). Ninguna persona real.' },
    mixto: { label: 'Mixto', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
      title: 'Mezcla de datos reales y de demostración.' },
    real: { label: 'Datos reales', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
      title: 'Datos vivos de la organización.' },
    sin_datos: { label: 'Sin datos', cls: 'bg-muted text-muted-foreground border-border', title: 'No hay datos en el periodo.' },
    no_disponible: { label: 'No conectado', cls: 'bg-muted text-muted-foreground border-border', title: 'La dependencia aún no está conectada.' },
    proximamente: { label: 'Próximamente', cls: 'bg-primary/10 text-primary border-primary/25', title: 'Capa en construcción.' },
  };
  const m = meta[origen] ?? meta.sin_datos;
  return (
    <span title={m.title} className={`inline-flex shrink-0 items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold ${m.cls} ${className}`}>
      {origen === 'demostracion' && <FlaskConical size={11} aria-hidden />}
      {m.label}
    </span>
  );
}

export function NivelChip({ nivel }: { nivel: string }) {
  if (nivel === 'alerta') {
    return <span className="inline-flex items-center rounded-full bg-red-500/15 px-2 py-0.5 text-[11px] font-semibold text-red-700 dark:text-red-300">Alerta</span>;
  }
  if (nivel === 'vigilancia') {
    return <span className="inline-flex items-center rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-semibold text-amber-700 dark:text-amber-300">Vigilancia</span>;
  }
  return <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">Normal</span>;
}

export const NIVEL_COLOR: Record<string, string> = { alerta: '#EF4444', vigilancia: '#F59E0B', '': '#8B5CF6' };

/** Banner de cumplimiento (Nivel A · Lineamiento IA SNS 2026 · humano al mando). */
export function ComplianceBanner({ children }: { children?: ReactNode }) {
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-primary/25 bg-primary/5 px-4 py-3 text-[13px] text-foreground">
      <ShieldCheck size={18} className="mt-0.5 shrink-0 text-primary" aria-hidden />
      <div className="min-w-0">
        <p className="font-semibold">Nivel A · Lineamiento IA SNS 2026 · humano al mando</p>
        <p className="mt-0.5 text-muted-foreground">
          {children ?? 'Agenda, orientación con canalización a una persona, tablero epidemiológico poblacional y notas que el médico revisa y firma. La IA no diagnostica ni decide sobre nadie.'}
        </p>
      </div>
    </div>
  );
}

export function Stat({ label, value, sub }: { label: string; value: ReactNode; sub?: ReactNode }) {
  return (
    <div className="orion-card min-w-0 rounded-[18px] border border-border bg-card p-4">
      <p className="truncate text-xs font-medium text-muted-foreground">{label}</p>
      <p className="mt-1 text-[26px] font-semibold leading-none tracking-tight tabular-nums text-foreground">{value}</p>
      {sub && <p className="mt-1.5 text-xs text-muted-foreground">{sub}</p>}
    </div>
  );
}

export const fmt = (n: number | null | undefined) => (n == null ? '—' : n.toLocaleString('es-MX'));

export function fechaCorta(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' });
}
